import type { LabelType } from '~/model'
import { getLabelLinks } from '~/helpers/labelHelpers'

export type StepLink = {
  key: string
  link: string
  download: boolean
  dataTest?: string
}
type Step = {
  key: string
  stepLink?: StepLink
}

export const LabelMimeType = 'application/pdf'

export const getServicePointUrl = (addressCountryCode: string, translationCountryCode: string) => {
  /* eslint-disable no-else-return */
  if (addressCountryCode === 'NL') {
    return translationCountryCode === 'nl'
      ? 'https://www.dhlparcel.nl/nl/consument/dhl-servicepoints'
      : 'https://www.dhlparcel.nl/en/consumer/dhl-servicepoints'
  } else if (addressCountryCode === 'BE') {
    if (translationCountryCode === 'nl') {
      return 'https://www.dhlparcel.be/nl/particulieren/dhl-servicepoints'
    } else if (translationCountryCode === 'en') {
      return 'https://www.dhlparcel.be/en/consumer/dhl-servicepoints'
    } else if (translationCountryCode === 'fr') {
      return 'https://www.dhlparcel.be/fr/particuliers/dhl-servicepoints'
    }
  }
  return 'https://parcelshopfinder.dhlparcel.com/'
  /* eslint-enable no-else-return */
}

/**
 * The success page shows the steps to take after a successful return request. But the steps differ based upon a lot
 * of factors. This function calculates the steps to show on the page.
 *
 * @param isCcc whether the business unit is CCC
 * @param tipsUrl link to the tips page
 * @param servicePointUrl link to the service point page
 * @param postalOffice if this return is to a postal office
 * @param labelType i.e. 'QrCode' or 'PrintedLabel'
 */
export const getNextSteps = (
  isCcc: boolean,
  tipsUrl: string,
  servicePointUrl: string,
  postalOffice: boolean,
  labelType?: LabelType
): Step[] => {
  const defaultOrCccOrIntKey = (base: string) => {
    if (isCcc) {
      return postalOffice ? `${base}_ccc_int` : `${base}_ccc`
    }
    return base
  }

  const servicePointStepLink: StepLink = {
    key: defaultOrCccOrIntKey('success.servicepoint.find_service_point'), // text is the same for QrCode and Label
    link: servicePointUrl,
    dataTest: 'success-servicepoint',
    download: false,
  }

  const [hasLabels, labels] = getLabelLinks(labelType)

  return labelType === 'QrCode'
    ? [
        hasLabels // we have a QR, so we show the step with the "download QR" link
          ? {
              key: 'success.servicepoint.qrcode.step1_withQR',
              stepLink: labels.length > 0 ? labels[0] : undefined, // For some reason the response contains multiple `QR codes`. We grab the first one as being *the* QR code.
            }
          : {
              key: 'success.servicepoint.qrcode.step1_noQR',
            },
        { key: 'success.servicepoint.qrcode.step2' },
        {
          key: 'success.servicepoint.qrcode.step3',
        },
        {
          key: defaultOrCccOrIntKey('success.servicepoint.qrcode.step4'),
          stepLink: servicePointStepLink,
        },
      ]
    : [
        // labelType==='PrintedLabel'
        hasLabels // we have labels, so we show the step with the "download label" link
          ? {
              key: 'success.servicepoint.step1_withLabels',
              stepLink: labels.length > 0 ? labels[0] : undefined, // For some reason the response contains multiple `labels`. We grab the first one as being *the* label.
            }
          : { key: 'success.servicepoint.step1_noLabels' },
        {
          key: 'success.servicepoint.step2',
          stepLink: isCcc
            ? undefined
            : { key: 'success.servicepoint.tips', link: tipsUrl, dataTest: 'success-tips', download: false },
        },
        {
          key: defaultOrCccOrIntKey('success.servicepoint.step3'),
          stepLink: servicePointStepLink,
        },
      ]
}
